import { getFoodTypeLabel } from "@/lib/food-types";
import {
  getBusinessConfig,
} from "@/lib/business-config";
import type {
  CompanyBusinessType,
} from "@/types/company";
import type {
  ProductBusinessAttributes,
} from "@/types/product";
import type {
  PublicCompany,
  PublicProduct,
} from "@/types/store";

export type PublicItemDetail = {
  key: string;
  label: string;
  value: string;
};

type PresentationProduct = Pick<
  PublicProduct,
  | "name"
  | "item_kind"
  | "category_name"
  | "subcategory_name"
  | "reference_code"
  | "short_description"
  | "description"
  | "business_attributes"
  | "has_variations"
  | "variations"
>;

type PresentationCompany = Pick<
  PublicCompany,
  | "business_type"
  | "city"
  | "state"
>;

export type PublicItemPresentation = {
  singular: string;
  singularLower: string;
  plural: string;
  routeSegment: string;

  badge: string;
  location: string;
  description: string;

  details: PublicItemDetail[];
};

function formatInteger(
  value: number,
): string {
  return new Intl.NumberFormat(
    "pt-BR",
  ).format(value);
}

function normalizeDescription(
  value: string,
  limit = 255,
): string {
  const normalized =
    value.replace(/\s+/g, " ").trim();

  if (normalized.length <= limit) {
    return normalized;
  }

  return (
    normalized
      .slice(0, limit - 1)
      .replace(/\s+\S*$/, "")
      .trim() + "…"
  );
}


function getVehicleConditionLabel(
  value:
    | ProductBusinessAttributes["condition"],
): string {
  const labels: Record<string, string> = {
    new: "Novo",
    semi_new: "Seminovo",
    used: "Usado",
  };

  return value
    ? labels[value] ?? "Veículo"
    : "Veículo";
}

function getTransmissionLabel(
  value:
    | ProductBusinessAttributes["transmission"],
): string {
  const labels = {
    manual: "Manual",
    automatic: "Automático",
    cvt: "CVT",
    automated: "Automatizado",
    other: "Outro",
  };

  return value
    ? labels[value] ?? value
    : "";
}

export function getFuelLabel(
  value:
    | ProductBusinessAttributes["fuel_type"],
): string {
  const labels = {
    gasoline: "Gasolina",
    ethanol: "Etanol",
    flex: "Flex",
    diesel: "Diesel",
    hybrid: "Híbrido",
    electric: "Elétrico",
    other: "Outro",
  };

  return value
    ? labels[value] ?? value
    : "";
}

function getRealEstateDetails(
  attributes: ProductBusinessAttributes,
): PublicItemDetail[] {
  return [
    attributes.area_m2
      ? {
          key: "area",
          label: "Área",
          value:
            `${attributes.area_m2} m²`,
        }
      : null,

    attributes.bedrooms
      ? {
          key: "bedrooms",
          label:
            attributes.bedrooms === 1
              ? "Dormitório"
              : "Dormitórios",
          value: String(
            attributes.bedrooms,
          ),
        }
      : null,

    attributes.parking_spaces !==
        undefined &&
      attributes.parking_spaces !== null
      ? {
          key: "parking",
          label:
            attributes.parking_spaces === 1
              ? "Vaga"
              : "Vagas",
          value: String(
            attributes.parking_spaces,
          ),
        }
      : null,
  ].filter(
    (item): item is PublicItemDetail =>
      Boolean(item),
  );
}

function getVehicleDetails(
  attributes: ProductBusinessAttributes,
): PublicItemDetail[] {
  const manufactureYear =
    attributes.manufacture_year;

  const modelYear =
    attributes.model_year;

  const year =
    manufactureYear && modelYear
      ? `${manufactureYear}/${modelYear}`
      : String(
          modelYear ??
            manufactureYear ??
            "",
        );

  return [
    year
      ? {
          key: "year",
          label: "Ano",
          value: year,
        }
      : null,

    attributes.mileage !== undefined
      ? {
          key: "mileage",
          label: "Quilometragem",
          value:
            `${formatInteger(
              attributes.mileage,
            )} km`,
        }
      : null,

    attributes.transmission
      ? {
          key: "transmission",
          label: "Câmbio",
          value:
            getTransmissionLabel(
              attributes.transmission,
            ),
        }
      : attributes.fuel_type
        ? {
            key: "fuel",
            label: "Combustível",
            value:
              getFuelLabel(
                attributes.fuel_type,
              ),
          }
        : null,
  ].filter(
    (item): item is PublicItemDetail =>
      Boolean(item),
  );
}

function getFoodDetails(
  attributes: ProductBusinessAttributes,
): PublicItemDetail[] {
  return [
    attributes.food_type
      ? {
          key: "food_type",
          label: "Tipo",
          value:
            getFoodTypeLabel(
              attributes.food_type,
            ),
        }
      : null,

    attributes.preparation_time_minutes
      ? {
          key: "preparation",
          label: "Preparo",
          value:
            `${attributes.preparation_time_minutes} min`,
        }
      : null,

    attributes.serves_people
      ? {
          key: "serves",
          label: "Serve",
          value:
            `${attributes.serves_people} pessoa${attributes.serves_people === 1 ? "" : "s"}`,
        }
      : null,
  ].filter(
    (item): item is PublicItemDetail =>
      Boolean(item),
  );
}

function getGenericDetails(
  product: PresentationProduct,
): PublicItemDetail[] {
  return [
    product.category_name
      ? {
          key: "category",
          label: "Categoria",
          value:
            product.category_name,
        }
      : null,

    product.subcategory_name
      ? {
          key: "subcategory",
          label: "Tipo",
          value:
            product.subcategory_name,
        }
      : null,

    product.reference_code
      ? {
          key: "reference",
          label: "Referência",
          value: product.reference_code,
        }
      : null,
  ]
    .filter(
      (item): item is PublicItemDetail =>
        Boolean(item),
    )
    .slice(0, 3);
}

function getBadge(
  businessType: CompanyBusinessType,
  product: PresentationProduct,
): string {
  const attributes =
    product.business_attributes;

  switch (businessType) {
    case "real_estate":
      return attributes.transaction_type ===
        "rent"
        ? "Para alugar"
        : "À venda";

    case "vehicles":
      return getVehicleConditionLabel(
        attributes.condition,
      );

    case "food":
      return getFoodTypeLabel(
        attributes.food_type,
      );

    case "services":
      return "Serviço";

    case "retail":
    case "wholesale":
      return (
        product.category_name ||
        "Produto"
      );

    default:
      return (
        product.category_name ||
        "Item"
      );
  }
}

function getLocation(
  businessType: CompanyBusinessType,
  product: PresentationProduct,
  company: PresentationCompany,
): string {
  if (businessType === "real_estate") {
    return [
      product.business_attributes
        .neighborhood,
      product.business_attributes.city,
      company.state,
    ]
      .filter(Boolean)
      .join(" - ");
  }

  return [
    company.city,
    company.state,
  ]
    .filter(Boolean)
    .join(" - ");
}

function getDetails(
  businessType: CompanyBusinessType,
  product: PresentationProduct,
): PublicItemDetail[] {
  switch (businessType) {
    case "real_estate":
      return getRealEstateDetails(
        product.business_attributes,
      );

    case "vehicles":
      return getVehicleDetails(
        product.business_attributes,
      );

    case "food":
      return getFoodDetails(
        product.business_attributes,
      );

    default:
      return getGenericDetails(product);
  }
}

export function getPublicItemPresentation(
  product: PresentationProduct,
  company: PresentationCompany,
): PublicItemPresentation {
  const businessType =
    company.business_type === "services" && product.item_kind === "product"
      ? "retail"
      : company.business_type;

  const config =
    getBusinessConfig(businessType);

  return {
    singular: config.itemSingular,
    singularLower:
      config.itemSingularLower,
    plural: config.itemPlural,
    routeSegment:
      config.publicItemPath,

    badge: getBadge(
      businessType,
      product,
    ),

    location: getLocation(
      businessType,
      product,
      company,
    ),

    description:
      normalizeDescription(
        product.description ||
          product.short_description ||
          "",
      ),

    details: getDetails(
      businessType,
      product,
    ),
  };
}
